import { Component, OnInit } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { GeographyService } from '../../../services/geography.service';
import { NotificationsService } from '../../../services/notifications.service';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { City, ServiceAndLocation, State } from '@voice/utils';
import { InputTextModule } from 'primeng/inputtext';
import { NgClass, NgIf } from '@angular/common';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-client-onboarding-location-address',
  standalone: true,
  templateUrl: './location-form.component.html',
  imports: [
    DialogModule,
    ButtonDirective,
    ReactiveFormsModule,
    InputTextModule,
    NgIf,
    NgClass,
    DropdownModule,
  ],
})
export class LocationFormComponent implements OnInit {
  record?: ServiceAndLocation;

  states!: State[];
  cities!: City[];

  selectedState?: State;
  selectedCity?: City;

  constructor(
    private readonly dialogRef: DynamicDialogRef,
    public readonly dialogConfig: DynamicDialogConfig,
    private readonly geographyService: GeographyService,
    private readonly notificationService: NotificationsService
  ) {}

  locationFormGroup!: FormGroup;

  ngOnInit(): void {
    this.record = this.dialogConfig.data.record;
    this.initializeStateAndCity()
      .then(() => this.initializeFormGroup())
      .catch((err) => this.notificationService.error(err));
  }

  isControlInvalid(controlName: string): boolean | undefined {
    return (
      this.locationFormGroup.get(controlName)?.invalid &&
      this.locationFormGroup.get(controlName)?.touched
    );
  }

  onStateChange(event: DropdownChangeEvent) {
    this.selectedState = event.value;
    this.fetchCities().then();
  }

  onCityChange(event: DropdownChangeEvent) {
    this.selectedCity = event.value;
  }

  save(): void {
    const result: ServiceAndLocation = {
      companyName: this.isNew
        ? this.locationFormGroup.get('companyName')?.value
        : this.record?.companyName || '',
      firstName: this.locationFormGroup.get('firstName')?.value,
      lastName: this.locationFormGroup.get('lastName')?.value,
      addressLine1: this.locationFormGroup.get('addressLineOne')?.value,
      addressLine2: this.locationFormGroup.get('addressLineTwo')?.value,
      state: this.selectedState?.code,
      city: this.selectedCity?.name,
      zipCode: this.locationFormGroup.get('zipCode')?.value,
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private initializeStateAndCity(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fetchStates()
        .then(() => {
          this.fetchCities()
            .then(() => resolve())
            .catch((err) => reject(err));
        })
        .catch((err) => reject(err));
    });
  }

  private fetchStates(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.geographyService.getStates().subscribe({
        next: (states: State[]) => {
          this.states = states;
          if (this.record?.state) {
            this.selectedState = this.states.find(
              (s) => s.code === this.record?.state
            );
          }
          resolve();
        },
        error: (err) => reject(err),
      });
    });
  }

  private fetchCities(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.selectedState) {
        this.geographyService.getCities(this.selectedState.code).subscribe({
          next: (cities: City[]) => {
            this.cities = cities;
            if (this.record?.city) {
              this.selectedCity = this.cities.find(
                (c) => c.name === this.record?.city
              );
            }
            resolve();
          },
          error: (err) => reject(err),
        });
      } else {
        resolve();
      }
    });
  }

  get isNew(): boolean {
    return !this.record?.companyName;
  }

  private initializeFormGroup() {
    this.locationFormGroup = new FormGroup({
      ...(this.isNew
        ? { companyName: new FormControl('', [Validators.required]) }
        : {}),
      firstName: new FormControl(this.record?.firstName, [Validators.required]),
      lastName: new FormControl(this.record?.lastName, [Validators.required]),
      addressLineOne: new FormControl(
        this.record?.addressLine1,
        Validators.required
      ),
      addressLineTwo: new FormControl(this.record?.addressLine2),
      state: new FormControl(this.selectedState, Validators.required),
      city: new FormControl(this.selectedCity, Validators.required),
      zipCode: new FormControl(this.record?.zipCode, [
        Validators.required,
        Validators.pattern('[0-9]{5}(?:-[0-9]{4})?'),
      ]),
    });
  }
}
